import { useState, useEffect } from 'react';
import { Eye, X, Search } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminAPI } from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';

const STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled'];
const sColor = s => ({ pending:'#f59e0b', confirmed:'#22c55e', processing:'#7c6dfa', shipped:'#38bdf8', delivered:'#22c55e', cancelled:'#f43f5e' }[s]||'#f59e0b');

export default function AdminOrders() {
  const { t } = useSettings();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [tracking, setTracking] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetch = (status) => {
    setLoading(true);
    adminAPI.getOrders(status ? { status } : {}).then(r => setOrders(r.data.orders)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(filter); }, [filter]);

  const handleUpdate = async () => {
    if (!newStatus) return;
    setUpdating(true);
    try {
      await adminAPI.updateOrderStatus(selected._id, { status: newStatus, trackingNumber: tracking });
      toast.success('Order updated!');
      setSelected(null); fetch(filter);
    } catch { toast.error('Error'); }
    setUpdating(false);
  };

  const filtered = orders.filter(o => !search || o.orderNumber?.toLowerCase().includes(search.toLowerCase()) || o.user?.name?.toLowerCase().includes(search.toLowerCase()) || o.user?.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <AdminLayout title={t('admin','orders')}>
      <div style={{ marginBottom: 20 }}>
        <div className="admin-orders-filters" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <button className={`btn btn-sm ${!filter?'btn-primary':'btn-secondary'}`} onClick={() => setFilter('')}>All ({orders.length})</button>
          {STATUSES.map(s => (
            <button key={s} className={`btn btn-sm ${filter===s?'btn-primary':'btn-secondary'}`} onClick={() => setFilter(s)} style={{ textTransform: 'capitalize' }}>{s}</button>
          ))}
        </div>
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <Search size={16} style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }} />
          <input className="form-input" placeholder="Search order or customer..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 42, paddingTop: 10, paddingBottom: 10 }} />
        </div>
      </div>

      {loading ? <div className="flex-center" style={{ height: 300 }}><div className="spinner" /></div> : (
        <div className="card admin-table-wrap" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table">
            <thead><tr><th>Order</th><th>Customer</th><th>Items</th><th>Total</th><th>{t('admin','status')}</th><th>Payment</th><th>Date</th><th>{t('admin','actions')}</th></tr></thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o._id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: 'var(--accent)' }}>#{o.orderNumber?.slice(-10)}</td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{o.user?.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.user?.email}</div>
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{o.items?.length} items</td>
                  <td style={{ color: 'var(--gold)', fontWeight: 700 }}>${o.totalPrice?.toFixed(2)}</td>
                  <td>
                    <span style={{ padding: '5px 12px', background: `${sColor(o.status)}18`, color: sColor(o.status), borderRadius: 8, fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>{o.status}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: 12, color: o.isPaid ? 'var(--success)' : 'var(--warning)', fontWeight: 600 }}>
                      {o.isPaid ? '✓ Paid' : '⏳ Pending'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{new Date(o.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(o); setNewStatus(o.status); setTracking(o.trackingNumber || ''); }}>
                      <Eye size={14} /> Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No orders found</div>}
        </div>
      )}

      {selected && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="modal-box" style={{ maxWidth: 540 }}>
            <div className="modal-header">
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>#{selected.orderNumber}</h2>
                <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{selected.user?.name} · {selected.user?.email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="btn btn-ghost btn-icon"><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20, padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: 14 }}>
                <div><div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total</div><div style={{ color: 'var(--gold)', fontWeight: 800, fontSize: 20 }}>${selected.totalPrice?.toFixed(2)}</div></div>
                <div><div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Payment</div><div style={{ color: selected.isPaid ? 'var(--success)' : 'var(--warning)', fontWeight: 700 }}>{selected.isPaid ? '✓ Paid' : '⏳ Pending'}</div></div>
                <div><div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Method</div><div style={{ fontSize: 14 }}>{selected.paymentMethod}</div></div>
                <div><div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Items</div><div style={{ fontSize: 14 }}>{selected.items?.length}</div></div>
              </div>
              <div className="form-group">
                <label className="form-label">Update Status</label>
                <select className="form-input" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tracking Number</label>
                <input className="form-input" value={tracking} onChange={e => setTracking(e.target.value)} placeholder="e.g. 1Z999AA1..." />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setSelected(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleUpdate} disabled={updating}>{updating ? 'Updating...' : 'Update Order'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
